import { gql, useMutation, useQuery } from '@apollo/client'
import { RouteComponentProps } from '@reach/router'
import cx from 'classnames'
import { format, parseISO } from 'date-fns'
import { Link } from 'gatsby'
import { without } from 'lodash'
import { useState } from 'react'
import { FaPlus } from 'react-icons/fa'
import { DashboardTitle } from './DashboardTitleContext'
import RapidSessionList, { rapidSessionFragment } from './RapidTesting/RapidSessionList'
import ThirdPartyKitUsage from './ThirdPartyKitUsage'
import {
  ThirdPartyDiscountCodesQuery,
  ThirdPartyDiscountCodesQueryVariables,
} from './__generated__/ThirdPartyDiscountCodesQuery'

const formatDate = (dateString: string) => new Date(dateString).toLocaleString()

const formatDateOnly = (dateString: string) => format(parseISO(dateString), 'dd/MM/yyyy')

const rapidSessionsQuery = gql`
  query RapidSessionsForThirdParty($thirdPartyId: ID!) {
    thirdPartyById(id: $thirdPartyId) {
      rapidSessions {
        ...RapidSessionListFragment
      }
    }
  }

  ${rapidSessionFragment}
`

const discountCodesQuery = gql`
  query ThirdPartyDiscountCodesQuery($thirdPartyId: ID!) {
    thirdPartyById(id: $thirdPartyId) {
      discountCodes {
        code
        status
        createdAt
        createdBy
        batchName
        usedOn
        usedForOrder {
          id
        }
        revokedOn
        revokedBy
        revokedReason
        multiUse
        startDate
        endDate
        useCount
      }
    }
  }
`

const kitsQuery = gql`
  query KitsForThirdParty($thirdPartyId: ID!) {
    thirdPartyById(id: $thirdPartyId) {
      kits {
        id
        status
        resultDate
        sku
      }
    }
  }
`

const revokeDiscountCodesMutation = gql`
  mutation RevokeDiscountCodes($codes: [ID!]!, $reason: String!) {
    revokeDiscountCodes(codes: $codes, reason: $reason) {
      codes {
        code
        status
        revokedOn
        revokedBy
        revokedReason
      }
    }
  }
`

const RapidSessions = ({ thirdPartyId }) => {
  const { loading, error, data } = useQuery(rapidSessionsQuery, {
    variables: { thirdPartyId },
  })

  if (loading) {
    return <p>Loading...</p>
  }

  if (error) {
    return <p>ERROR {JSON.stringify(error, null, 2)}</p>
  }

  return (
    <>
      <nav className="level has-width-unset">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5">Rapid Sessions for {thirdPartyId}</p>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <Link className="button is-primary" to={`/dashboard/third-party/${thirdPartyId}/sessions/new`}>
              <span className="icon">
                <FaPlus />
              </span>
              <span>New Session</span>
            </Link>
          </div>
        </div>
      </nav>

      <RapidSessionList sessions={data.thirdPartyById.rapidSessions} />
    </>
  )
}
const DiscountCodes = ({ thirdPartyId }) => {
  const [selectedCodes, setSelectedCodes] = useState<string[]>([])

  const { loading, error, data } = useQuery<ThirdPartyDiscountCodesQuery, ThirdPartyDiscountCodesQueryVariables>(
    discountCodesQuery,
    {
      variables: { thirdPartyId },
      fetchPolicy: 'cache-and-network',
    }
  )

  const [revokeCodes] = useMutation(revokeDiscountCodesMutation)

  if (loading) {
    return <p>Loading...</p>
  }

  if (error) {
    return <p>ERROR {JSON.stringify(error, null, 2)}</p>
  }

  const onMultiRevoke = async () => {
    const reason = window.prompt(`Provide a reason for revoking ${selectedCodes.length} discount codes`)

    if (!reason) {
      return
    }

    await revokeCodes({ variables: { codes: selectedCodes, reason } })
  }

  return (
    <>
      <nav className="level has-width-unset">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5">Discount codes for {thirdPartyId}</p>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <Link className="button is-primary" to={`/dashboard/generate-discount-codes?thirdPartyId=${thirdPartyId}`}>
              <span className="icon">
                <FaPlus />
              </span>
              <span>New Discount Code(s)</span>
            </Link>
          </div>
        </div>
      </nav>
      {selectedCodes.length > 0 && (
        <div>
          <button
            className={cx('button is-small is-danger is-outlined', {
              'is-loading': false,
            })}
            disabled={false}
            onClick={onMultiRevoke}
          >
            Revoke {selectedCodes.length} discount codes
          </button>
        </div>
      )}
      <table className="table is-bordered is-fullwidth" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ width: '2.5rem' }}></th>
            <th>Code</th>
            <th>Status</th>
            <th>Detail</th>
            <th>Created</th>
            <th>Reference / Batch Name</th>
          </tr>
        </thead>
        <tbody>
          {data.thirdPartyById.discountCodes.map((code) => (
            <tr key={code.code}>
              <td>
                {code.status === 'AVAILABLE' && (
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      const checked = e.target.checked
                      setSelectedCodes((prev) => {
                        if (checked) {
                          return [...prev, code.code]
                        }

                        return without(prev, code.code)
                      })
                    }}
                  />
                )}
              </td>
              <td>{code.code}</td>
              <td>{code.status}</td>
              <td>
                {code.multiUse ? (
                  <Link to={`/dashboard/third-party/${thirdPartyId}/discount-code/${code.code}`}>
                    <strong>Multi-use</strong> {code.useCount} kits
                  </Link>
                ) : (
                  <>
                    {code.usedForOrder && (
                      <>
                        <Link to={`/dashboard/kits?kitId=${code.usedForOrder.id.replace('Order', 'Kit')}`}>
                          {code.usedForOrder.id}
                        </Link>
                        <br />
                        <strong>Used: </strong>
                        {formatDate(code.usedOn)}
                      </>
                    )}
                  </>
                )}

                {code.revokedOn && (
                  <>
                    <br />
                    <strong>Revoked: </strong>
                    {formatDate(code.revokedOn)} by {code.revokedBy} because "{code.revokedReason}"
                  </>
                )}
                {code.startDate && (
                  <>
                    <br />
                    <strong>Valid: </strong>
                    {formatDateOnly(code.startDate)} - {formatDateOnly(code.endDate)}
                  </>
                )}
              </td>
              <td>
                {formatDateOnly(code.createdAt)} {code.createdBy}
              </td>
              <td>{code.batchName}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

const Kits = ({ thirdPartyId }) => {
  const { loading, error, data } = useQuery(kitsQuery, {
    variables: { thirdPartyId },
  })

  if (loading) {
    return <p>Loading...</p>
  }

  if (error) {
    return <p>ERROR {JSON.stringify(error, null, 2)}</p>
  }

  return (
    <>
      <nav className="level has-width-unset">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5">Kits for {thirdPartyId}</p>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <Link className="button is-primary" to={`/dashboard/generate-kit-ids?thirdPartyId=${thirdPartyId}`}>
              <span className="icon">
                <FaPlus />
              </span>
              <span>Generate Kit IDs</span>
            </Link>
          </div>
        </div>
      </nav>

      <table className="table is-bordered is-fullwidth" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th>ID</th>
            <th>SKU</th>
            <th>Status</th>
            <th>Result date</th>
          </tr>
        </thead>
        <tbody>
          {data.thirdPartyById.kits.map((kit) => (
            <tr key={kit.id}>
              <td>
                <Link to={`/dashboard/kits?kitId=${kit.id}`}>{kit.id}</Link>
              </td>
              <td>{kit.sku || 'unknown'}</td>
              <td>{kit.status}</td>
              <td>{kit.resultDate && formatDate(kit.resultDate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}
const ThirdParty = ({ id }: RouteComponentProps<{ id: string }>) => {
  return (
    <>
      <nav className="breadcrumb ml-6" aria-label="breadcrumbs">
        <ul>
          <li>
            <Link to="/dashboard/third-parties">Third Parties</Link>
          </li>
          <li className="is-active">
            <a href={`/dashboard/third-party/${id}`} aria-current="page">
              {id}
            </a>
          </li>
        </ul>
      </nav>

      <div className="container">
        <DashboardTitle title={`Third Party: ${id}`} />

        <RapidSessions thirdPartyId={id} />
        <DiscountCodes thirdPartyId={id} />
        <Kits thirdPartyId={id} />

        <p className="subtitle is-5">Kit Usage Report</p>
        <ThirdPartyKitUsage thirdPartyId={id} />
      </div>
    </>
  )
}

export default ThirdParty
